import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Combobox, type ComboboxOption } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { listCustomers, useCustomerDetail, type Customer } from '@/api/customers';

/** One-line customer label: `#id - company|name · email` (legacy option format).
 * Company field differs between the list row (`company_name`) and the hydrated
 * detail (`customer_company_name`), so read both. */
function customerLine(c: Customer): string {
  const withCo = c as Customer & { company_name?: string; customer_company_name?: string };
  const company = withCo.company_name || withCo.customer_company_name;
  const primary = company || c.customer_name || c.customer_email || `Customer #${c.customer_id}`;
  const email = c.customer_email && c.customer_email !== primary ? ` · ${c.customer_email}` : '';
  return `#${c.customer_id} - ${primary}${email}`;
}

/**
 * Async customer picker for the contract wizard. Debounces the typed term,
 * queries `GET /customers?q=`, and on select stores the numeric `customer_id`.
 * Built on the shared @ui `Combobox` (search + list + clear + keyboard nav);
 * this component owns only the data-fetching, debounce, and deep-link hydration.
 */
export function CustomerSearchSelect({
  value,
  onChange,
  label,
  required,
  error,
}: {
  value: number;
  onChange: (id: number) => void;
  label: string;
  required?: boolean;
  error?: string;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState<Customer | null>(null);

  // Debounce the search term.
  useEffect(() => {
    const h = setTimeout(() => setDebounced(term.trim()), 300);
    return () => clearTimeout(h);
  }, [term]);

  // Hydrate the display label when `customer_id` is set externally (deep-link URL
  // prefill) rather than via selection, so the customer shows as selected.
  const needsHydration = value > 0 && (!selected || selected.customer_id !== value);
  const { data: hydrated } = useCustomerDetail(needsHydration ? value : 0);
  useEffect(() => {
    if (!needsHydration) return;
    const c = hydrated?.result as Customer | undefined;
    if (c && c.customer_id === value) setSelected(c);
  }, [needsHydration, hydrated, value]);

  const { data, isFetching } = useQuery({
    queryKey: ['contract-customer-search', debounced],
    enabled: debounced.length >= 2,
    queryFn: () => listCustomers({ q: debounced, page: 1, page_size: 8 }),
  });
  const results = useMemo(() => data?.results ?? [], [data]);

  // Keep the selected customer available so its label renders in the trigger even
  // after the results list changes; server owns the filtering, so options pass
  // straight through to Combobox.
  const customersById = useMemo(() => {
    const m = new Map<number, Customer>();
    if (selected) m.set(selected.customer_id, selected);
    for (const c of results) m.set(c.customer_id, c);
    return m;
  }, [results, selected]);

  const options: ComboboxOption[] = useMemo(
    () =>
      [...customersById.values()].map((c) => ({
        value: String(c.customer_id),
        label: customerLine(c),
      })),
    [customersById],
  );

  const emptyHint =
    debounced.length < 2
      ? 'Type at least 2 characters to search.'
      : 'No customers found.';

  return (
    <Field label={label} required={required} error={error}>
      {() => (
        <Combobox
          options={options}
          value={value > 0 ? String(value) : null}
          onChange={(v) => {
            if (!v) {
              setSelected(null);
              setTerm('');
              onChange(0);
              return;
            }
            setSelected(customersById.get(Number(v)) ?? null);
            onChange(Number(v));
          }}
          inputValue={term}
          onInputChange={setTerm}
          loading={isFetching && debounced.length >= 2}
          placeholder="Search customers by name, email, phone…"
          searchPlaceholder="Search customers by name, email, phone…"
          emptyHint={emptyHint}
          invalid={!!error}
          aria-label={label}
        />
      )}
    </Field>
  );
}
